import React from 'react';
import { SvgIconProps } from '@mui/material';
import { InformationOutline as InformationOutlineIcon } from 'mdi-material-ui';
import { Stack } from '../Stack';
import { Tooltip } from '../Tooltip';
import { Typography } from '../Typography';
import { CollapsibleIconButton } from '../Collapsible/CollapsibleIconButton';
import { Markdown } from '../Markdown';
import { styled } from '../../utils';
import { useContainerContext } from './ContainerContextProvider';
import { InternalContainerActions } from './ContainerActions';
import { useContainerLayout } from './useContainerLayout';

export type ContainerHeaderVariant = 'subtle' | 'strong';

export interface ContainerHeaderProps {
  /**
   * Content of the heading.
   */
  children: React.ReactNode;

  /**
   * Optional action elements (e.g. buttons, filters, etc) that are rendered in the top right corner of the header.
   * These actions are wrapped in a Stack to ensure conistent spacing so you can simply pass in the elements.
   *
   * WARNING: this prop is deprecated and may not support new actions-based functionality. Use the `actions` prop
   * on `Container` instead.
   *
   * @deprecated
   */
  actions?: React.ReactNode;

  /**
   * The container description. Rendered in a tooltip after the header title.
   */
  description?: string;
  /**
   * Optional slot to display an extra component beside the container header content.
   * It will show to the right of the description icon when description is populated.
   */
  endAdornment?: React.ReactNode;
  /**
   * An optional icon that renders before the title
   */
  Icon?: React.ElementType<SvgIconProps>;
  /**
   * Whether to use the subtle or strong header. The subtle header has smaller text.
   * The strong header has larger text.
   * @default 'strong'
   */
  variant?: ContainerHeaderVariant;
}

const StyledMarkdown = styled(Markdown)({
  'p:last-child': {
    marginBottom: 0,
  },
});

export const ContainerHeader = ({
  children,
  description,
  endAdornment,
  Icon,
  variant = 'strong',
  actions: deprecatedActions,
}: ContainerHeaderProps) => {
  const { onCollapseButtonClick, collapsed, subtleActionsVisibility, isCollapsible, collapseButtonAriaLabel } =
    useContainerContext();

  const actionsVisibility = variant === 'strong' ? 'always' : subtleActionsVisibility || 'always';

  const { header: headerLayout } = useContainerLayout();

  return (
    <Stack
      direction="row"
      sx={{
        minHeight: `${headerLayout.height}px`,
        width: '100%',
      }}
      justifyContent="space-between"
      spacing={1}
      className="container-header"
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        className="container-title"
        alignSelf="flex-start"
        sx={{ paddingTop: 0.5, flex: 1, width: 0, cursor: isCollapsible ? 'pointer' : undefined }}
        onClick={
          isCollapsible
            ? () => {
                onCollapseButtonClick?.(!collapsed);
              }
            : undefined
        }
      >
        {isCollapsible && (
          <CollapsibleIconButton
            collapsed={collapsed}
            ariaLabelSubject={collapseButtonAriaLabel ?? 'container'}
            noPadding
            size="medium"
          />
        )}
        {Icon && <Icon sx={{ fontSize: '16px' }} />}
        <Typography
          variant="h3"
          noWrap
          sx={(theme) => ({
            fontSize: variant === 'subtle' ? theme.typography.h4.fontSize : undefined,
            fontWeight: variant === 'subtle' ? theme.typography.fontWeightRegular : undefined,
          })}
        >
          {children}
        </Typography>
        {/* Show the info icon when description is defined and is not all whitespace */}
        {description !== undefined && description.trim().length > 0 && (
          <Tooltip Content={<StyledMarkdown>{description}</StyledMarkdown>}>
            <InformationOutlineIcon
              aria-label={description}
              aria-describedby="info-icon"
              aria-hidden={false}
              fontSize="small"
              sx={{ color: (theme) => theme.palette.text.secondary, width: '20px' }}
              tabIndex={0}
            />
          </Tooltip>
        )}
        {endAdornment}
      </Stack>
      <InternalContainerActions actionsVisibility={actionsVisibility} deprecatedActions={deprecatedActions} />
    </Stack>
  );
};
